// PrintingInit.tsx
import { useEffect } from 'react';
import { App } from '@capacitor/app';

import { starPrintService } from 'src/services/printers';
import { usePrinterStore } from 'src/store/PrinterStore';
import {
  usePrintingEnabled,
  checkPrinterReadiness,
  usePrinterAutoReconnect,
} from 'src/services/printing';

/**
 * Boots the central printing flow on app load. On mount it always hydrates the
 * active + remembered printers from local storage (the print flow needs them
 * regardless of the readiness feature).
 *
 * The *proactive readiness* work — verifying the saved printer, listening for
 * live status, and re-checking on app resume — only runs for businesses with
 * printing enabled in the Account Access V2 policy. Because the business profile
 * loads after login, this is keyed on `usePrintingEnabled()` and re-runs when
 * the gate flips. None of it opens a modal; it only updates printer state.
 * Renders nothing.
 */
export function PrintingInit() {
  const printingEnabled = usePrintingEnabled();

  // Silently reconnect the saved printer after it has been unreachable for a
  // grace period (native-only; inert while printing is disabled).
  usePrinterAutoReconnect(printingEnabled);

  useEffect(() => {
    let cancelled = false;
    let unsubscribeStatus: (() => void) | null = null;
    let removeAppListener: (() => void) | null = null;

    const init = async () => {
      const { loadPrinters, setIsPrinterReady } = usePrinterStore.getState();
      // Always hydrate printers (migrates the legacy single default printer).
      await loadPrinters();
      if (cancelled) return;

      // Readiness checks are gated to printing-enabled businesses only.
      if (!printingEnabled) {
        setIsPrinterReady(false);
        return;
      }

      // One forced check now (bypasses the throttle), then live updates.
      await checkPrinterReadiness({ force: true });
      if (cancelled) return;

      unsubscribeStatus = await starPrintService.addStatusListener((status) => {
        if (!cancelled) usePrinterStore.getState().applyStatus(status);
      });

      // Re-verify when the app returns to the foreground (throttled).
      try {
        const handle = await App.addListener('appStateChange', ({ isActive }) => {
          if (isActive && !cancelled) checkPrinterReadiness();
        });
        removeAppListener = () => handle.remove();
      } catch {
        // appStateChange is unavailable on some platforms (e.g. web) — ignore.
      }
    };

    init();

    return () => {
      cancelled = true;
      if (unsubscribeStatus) unsubscribeStatus();
      if (removeAppListener) removeAppListener();
    };
  }, [printingEnabled]);

  return null;
}
